import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Badge,
  Button,
  Card,
  Checkbox,
  Collapse,
  Group,
  NumberInput,
  ScrollArea,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { openConfirmModal } from '@mantine/modals'
import { showNotification } from '@mantine/notifications'
import { ActionButton, AppModal } from '@/components/ui'
import { api, ApiError, DEFAULT_ERROR_NOTIFY_MESSAGE } from '@/lib/api'
import { downloadBlob } from '@/lib/download-blob'
import type { AIInstructionRow, AIInstructionWritePayload, ProjectAISettingsResponse } from '@/types/api'

type Props = {
  projectId: string
  canEdit: boolean
  refreshKey: number
  settings: ProjectAISettingsResponse | null
  onSettingsUpdated: () => void
  /** Вкладка «Инструкции» открыта — подгружаем актуальный системный промпт */
  tabActive?: boolean
}

function snippet(s: string | null | undefined, n: number) {
  const t = (s || '').trim()
  if (t.length <= n) return t
  return `${t.slice(0, n)}…`
}

function isTemporaryInstruction(r: AIInstructionRow): boolean {
  const extra = r.extra_data
  return Boolean(extra && typeof extra === 'object' && extra.temporary)
}

export function AiInstructionsTab({
  projectId,
  canEdit,
  refreshKey,
  settings,
  onSettingsUpdated,
  tabActive = true,
}: Props) {
  const compactLayout = useMediaQuery('(max-width: 1200px)')
  const [rows, setRows] = useState<AIInstructionRow[]>([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [fCat, setFCat] = useState('')
  const [fTrig, setFTrig] = useState('')
  const [fAct, setFAct] = useState('')
  const [fTags, setFTags] = useState('')
  const [fPri, setFPri] = useState('')
  const [fStat, setFStat] = useState('')
  const fileRef = useRef<HTMLInputElement>(null)
  const [modal, setModal] = useState<AIInstructionRow | 'new' | null>(null)
  const [saving, setSaving] = useState(false)

  const [category, setCategory] = useState('')
  const [triggers, setTriggers] = useState('')
  const [action, setAction] = useState('')
  const [responseFromDb, setResponseFromDb] = useState('')
  const [dataCollected, setDataCollected] = useState('')
  const [questionsAsked, setQuestionsAsked] = useState('')
  const [tags, setTags] = useState('')
  const [priority, setPriority] = useState('')
  const [order, setOrder] = useState<number | string>(0)
  const [whenTransfer, setWhenTransfer] = useState('')
  const [messageClient, setMessageClient] = useState('')
  const [isActive, setIsActive] = useState(true)
  const [systemPrompt, setSystemPrompt] = useState('')
  const [generatedPrompt, setGeneratedPrompt] = useState('')
  const [systemPromptSource, setSystemPromptSource] = useState<'project' | 'assistant' | 'none' | null>(null)
  const [savingSystemPrompt, setSavingSystemPrompt] = useState(false)
  const [loadingSystemPrompt, setLoadingSystemPrompt] = useState(false)
  const systemPromptDirtyRef = useRef(false)

  const pickCustomSystemPrompt = useCallback((ai: ProjectAISettingsResponse | null | undefined) => {
    if (!ai) return ''
    const custom = ai.instructions_system_prompt_saved ?? ai.instructions_system_prompt
    return typeof custom === 'string' ? custom : ''
  }, [])

  const applySystemPromptFromAi = useCallback(
    (ai: ProjectAISettingsResponse | null | undefined) => {
      setSystemPrompt(pickCustomSystemPrompt(ai))
      setGeneratedPrompt(
        typeof ai?.instructions_system_prompt_generated === 'string' ? ai.instructions_system_prompt_generated : '',
      )
      setSystemPromptSource(ai?.instructions_system_prompt_source ?? null)
    },
    [pickCustomSystemPrompt],
  )

  useEffect(() => {
    if (systemPromptDirtyRef.current) return
    applySystemPromptFromAi(settings)
  }, [settings, refreshKey, applySystemPromptFromAi])

  useEffect(() => {
    if (!tabActive || !projectId) return
    let cancelled = false
    setLoadingSystemPrompt(true)
    void api
      .projectAI(projectId)
      .then((ai) => {
        if (cancelled || systemPromptDirtyRef.current) return
        applySystemPromptFromAi(ai)
      })
      .catch(() => {
        /* оставляем значение из settings */
      })
      .finally(() => {
        if (!cancelled) setLoadingSystemPrompt(false)
      })
    return () => {
      cancelled = true
    }
  }, [tabActive, projectId, refreshKey, applySystemPromptFromAi])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const list = await api.aiInstructionsList(projectId)
      setRows(Array.isArray(list) ? list : [])
      setSelected(new Set())
    } catch {
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [projectId])

  useEffect(() => {
    void load()
  }, [load, refreshKey])

  const filtered = rows.filter((r) => {
    if (fCat && !(r.category || '').toLowerCase().includes(fCat.toLowerCase())) return false
    if (fTrig && !(r.triggers || '').toLowerCase().includes(fTrig.toLowerCase())) return false
    if (fAct && !(r.action || '').toLowerCase().includes(fAct.toLowerCase())) return false
    if (fTags && !(r.tags || '').toLowerCase().includes(fTags.toLowerCase())) return false
    if (fPri && !(r.priority || '').toLowerCase().includes(fPri.toLowerCase())) return false
    if (fStat && !(r.status_on_transfer || '').toLowerCase().includes(fStat.toLowerCase())) return false
    return true
  })

  const openNew = () => {
    setModal('new')
    setCategory('')
    setTriggers('')
    setAction('')
    setResponseFromDb('')
    setDataCollected('')
    setQuestionsAsked('')
    setTags('')
    setPriority('')
    setOrder(rows.length)
    setWhenTransfer('')
    setMessageClient('')
    setIsActive(true)
  }

  const openEdit = (r: AIInstructionRow) => {
    setModal(r)
    setCategory(r.category || '')
    setTriggers(r.triggers || '')
    setAction(r.action || '')
    setResponseFromDb(r.response_from_db || '')
    setDataCollected(r.data_collected || '')
    setQuestionsAsked(r.questions_asked || '')
    setTags(r.tags || '')
    setPriority(r.priority || '')
    setOrder(r.order ?? 0)
    setWhenTransfer(r.when_transfer_to_operator || '')
    setMessageClient(r.message_to_client_on_transfer || '')
    setIsActive(r.is_active !== false)
  }

  const editingTemporary = Boolean(modal) && modal !== 'new' && modal != null && isTemporaryInstruction(modal)

  const saveInstruction = async () => {
    const body: AIInstructionWritePayload = {
      category: category.trim() || null,
      triggers: triggers.trim() || null,
      action: action.trim() || null,
      response_from_db: responseFromDb.trim() || null,
      data_collected: dataCollected.trim() || null,
      questions_asked: questionsAsked.trim() || null,
      tags: tags.trim() || null,
      priority: priority.trim() || null,
      order: typeof order === 'number' ? order : parseInt(String(order), 10) || 0,
      when_transfer_to_operator: whenTransfer.trim() || null,
      message_to_client_on_transfer: messageClient.trim() || null,
      is_active: isActive,
    }
    if (!editingTemporary) {
      body.is_active = isActive
    }
    setSaving(true)
    try {
      if (modal === 'new') {
        await api.createAIInstruction(projectId, body)
      } else if (modal) {
        await api.updateAIInstruction(projectId, modal.id, body)
      }
      showNotification({ title: 'Сохранено', message: '', color: 'green' })
      setModal(null)
      await load()
    } catch (e) {
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : 'Не удалось сохранить',
        color: 'red',
      })
    } finally {
      setSaving(false)
    }
  }

  const toggleSel = (id: string) => {
    setSelected((prev) => {
      const n = new Set(prev)
      if (n.has(id)) n.delete(id)
      else n.add(id)
      return n
    })
  }

  const selectAllFiltered = () => {
    setSelected(new Set(filtered.map((r) => r.id)))
  }

  const exportExcel = async () => {
    try {
      const blob = await api.exportAIInstructionsExcel(projectId)
      downloadBlob(blob, `instructions-${projectId}.xlsx`)
    } catch (e) {
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : 'Экспорт не удался',
        color: 'red',
      })
    }
  }

  const saveSystemPrompt = async () => {
    if (!canEdit) return
    setSavingSystemPrompt(true)
    try {
      const updated = await api.projectAIUpdate(projectId, {
        instructions_system_prompt: systemPrompt.trim() || null,
      })
      applySystemPromptFromAi(updated)
      systemPromptDirtyRef.current = false
      showNotification({
        title: 'Системный промпт сохранён',
        message: 'После изменения выполните «Синхронизировать ассистента» на вкладке «Основные».',
        color: 'green',
      })
      onSettingsUpdated()
    } catch (e) {
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : 'Не удалось сохранить',
        color: 'red',
      })
    } finally {
      setSavingSystemPrompt(false)
    }
  }

  const onFile = async (f: File | null) => {
    if (!f || !canEdit) return
    try {
      const res = await api.uploadAIInstructionsExcel(projectId, f)
      showNotification({
        title: 'Импорт',
        message: (res as { message?: string })?.message || 'Готово',
        color: 'green',
      })
      await load()
    } catch (e) {
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : 'Импорт не удался',
        color: 'red',
      })
    } finally {
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  return (
    <Stack gap="md">
      <Card withBorder padding="md" radius="md">
        <Group justify="space-between" align="flex-start" mb="xs" wrap="nowrap">
          <Text fw={600}>Системный промпт</Text>
          {systemPromptSource === 'project' ? (
            <Badge variant="light" color="teal" size="sm">
              доп. блок проекта
            </Badge>
          ) : null}
        </Group>
        <Text size="xs" c="dimmed" mb="sm">
          Автопромпт проекта генерируется системой на основе инструкций, базы знаний, переменных и исключений. Ниже
          редактируется только дополнительный блок для особенностей конкретного проекта.
        </Text>
        <Textarea
          value={generatedPrompt}
          minRows={6}
          autosize
          maxRows={24}
          readOnly
          mb="sm"
          label="Автогенерируемый промпт проекта (read-only)"
        />
        <Textarea
          value={systemPrompt}
          onChange={(e) => {
            systemPromptDirtyRef.current = true
            setSystemPrompt(e.currentTarget.value)
          }}
          minRows={6}
          autosize
          maxRows={24}
          label="Дополнительные правила проекта (editable)"
          placeholder={
            loadingSystemPrompt ? 'Загрузка…' : 'Например: ограничения тона, спец-формулировки, редкие исключения.'
          }
          disabled={!canEdit || loadingSystemPrompt}
        />
        {canEdit ? (
          <Group justify="flex-end" mt="sm">
            <Button size="sm" loading={savingSystemPrompt} onClick={() => void saveSystemPrompt()}>
              Сохранить системный промпт
            </Button>
          </Group>
        ) : null}
      </Card>

      <input
        ref={fileRef}
        type="file"
        accept=".xlsx,.xls"
        style={{ display: 'none' }}
        onChange={(e) => void onFile(e.target.files?.[0] ?? null)}
      />
      <Group gap="xs" wrap="wrap">
        {canEdit ? (
          <Button size="sm" onClick={openNew}>
            Добавить инструкцию
          </Button>
        ) : null}
        <Button size="sm" variant="default" onClick={() => void exportExcel()}>
          Выгрузить Excel
        </Button>
        {canEdit ? (
          <Button size="sm" variant="light" onClick={() => fileRef.current?.click()}>
            Загрузить Excel
          </Button>
        ) : null}
        {rows.length > 0 ? (
          <Button size="sm" variant="default" onClick={() => setFiltersOpen((v) => !v)}>
            Фильтры
          </Button>
        ) : null}
      </Group>

      <Card withBorder padding="sm" radius="md">
        <Text fw={600} size="sm" mb={4}>
          Как работает сценарий
        </Text>
        <Text size="xs" c="dimmed">
          Добавьте триггеры через запятую, готовый ответ в «Ответ из базы», теги и приоритет — выбранный сценарий
          передаётся в LLM как обязательная инструкция. Для многоходового уточнения заполните «Какие данные собирает» и
          «Вопросы»: Мозгобот проверит историю, спросит только недостающее и будет опираться на актуальную базу знаний
          проекта.
        </Text>
      </Card>

      <Collapse in={filtersOpen}>
        <Card withBorder padding="sm" radius="md">
          <Group grow wrap="wrap">
            <TextInput label="Категория" value={fCat} onChange={(e) => setFCat(e.target.value)} />
            <TextInput label="Триггеры" value={fTrig} onChange={(e) => setFTrig(e.target.value)} />
            <TextInput label="Действие" value={fAct} onChange={(e) => setFAct(e.target.value)} />
            <TextInput label="Теги" value={fTags} onChange={(e) => setFTags(e.target.value)} />
            <TextInput label="Приоритет" value={fPri} onChange={(e) => setFPri(e.target.value)} />
            <TextInput label="Статус при передаче" value={fStat} onChange={(e) => setFStat(e.target.value)} />
          </Group>
          <Button
            size="xs"
            variant="subtle"
            mt="xs"
            onClick={() => {
              setFCat('')
              setFTrig('')
              setFAct('')
              setFTags('')
              setFPri('')
              setFStat('')
            }}
          >
            Сбросить
          </Button>
        </Card>
      </Collapse>

      {rows.length > 0 && canEdit ? (
        <Group gap="xs" wrap="wrap">
          <Button size="xs" variant="default" onClick={selectAllFiltered}>
            Выбрать все (отфильтр.)
          </Button>
          <Button
            size="xs"
            variant="default"
            onClick={() =>
              void api
                .activateAllAIInstructions(projectId)
                .then(() => load())
                .then(() => showNotification({ title: 'Все активированы', message: '', color: 'green' }))
                .catch((e) =>
                  showNotification({
                    title: 'Ошибка',
                    message: e instanceof ApiError ? e.message : DEFAULT_ERROR_NOTIFY_MESSAGE,
                    color: 'red',
                  }),
                )
            }
          >
            Все активны
          </Button>
          <Button
            size="xs"
            variant="default"
            disabled={selected.size === 0}
            onClick={() => {
              void api
                .bulkDeactivateAIInstructions(projectId, [...selected])
                .then(() => load())
                .then(() => showNotification({ title: 'Готово', message: '', color: 'green' }))
                .catch((e) =>
                  showNotification({
                    title: 'Ошибка',
                    message: e instanceof ApiError ? e.message : DEFAULT_ERROR_NOTIFY_MESSAGE,
                    color: 'red',
                  }),
                )
            }}
          >
            Снять активность с выбранных
          </Button>
          <Button
            size="xs"
            color="red"
            variant="light"
            onClick={() =>
              openConfirmModal({
                title: 'Удалить все инструкции?',
                labels: { confirm: 'Удалить', cancel: 'Отмена' },
                confirmProps: { color: 'red' },
                onConfirm: () =>
                  void api
                    .deleteAllAIInstructions(projectId)
                    .then(() => load())
                    .then(() => showNotification({ title: 'Удалено', message: '', color: 'green' }))
                    .catch((e) =>
                      showNotification({
                        title: 'Ошибка',
                        message: e instanceof ApiError ? e.message : DEFAULT_ERROR_NOTIFY_MESSAGE,
                        color: 'red',
                      }),
                    ),
              })
            }
          >
            Удалить все
          </Button>
          <Button
            size="xs"
            variant="light"
            onClick={() =>
              void api
                .createTagsFromAIInstructions(projectId)
                .then(() => showNotification({ title: 'Теги', message: 'Запрос выполнен', color: 'green' }))
                .catch((e) =>
                  showNotification({
                    title: 'Ошибка',
                    message: e instanceof ApiError ? e.message : DEFAULT_ERROR_NOTIFY_MESSAGE,
                    color: 'red',
                  }),
                )
            }
          >
            Теги из инструкций
          </Button>
        </Group>
      ) : null}

      <Text size="sm" c="dimmed">
        Показано {filtered.length} из {rows.length}
      </Text>

      {loading ? (
        <Text size="sm">Загрузка…</Text>
      ) : filtered.length === 0 ? (
        <Text size="sm" c="dimmed">
          Нет инструкций
        </Text>
      ) : compactLayout ? (
        <Stack gap="sm">
          {filtered.map((r) => (
            <Card key={r.id} withBorder radius="md" p="sm">
              <Stack gap={8}>
                <Group justify="space-between" align="center" wrap="nowrap">
                  <Group gap={8} wrap="nowrap">
                    {canEdit ? <Checkbox checked={selected.has(r.id)} onChange={() => toggleSel(r.id)} /> : null}
                    <Text fw={600} size="sm">
                      {snippet(r.category, 80) || '—'}
                    </Text>
                    <Badge variant="light" size="sm">
                      {r.is_active !== false ? 'Активна' : 'Неактивна'}
                    </Badge>
                    {isTemporaryInstruction(r) ? (
                      <Badge variant="outline" size="sm" color="orange">
                        Временная
                      </Badge>
                    ) : null}
                  </Group>
                  <Group gap={6} wrap="nowrap">
                    <Button size="compact-xs" variant="default" onClick={() => openEdit(r)}>
                      Изменить
                    </Button>
                    {canEdit ? (
                      <Button
                        size="compact-xs"
                        color="red"
                        variant="light"
                        onClick={() =>
                          openConfirmModal({
                            title: 'Удалить инструкцию?',
                            onConfirm: () =>
                              void api
                                .deleteAIInstruction(projectId, r.id)
                                .then(() => load())
                                .then(() => showNotification({ title: 'Удалено', message: '', color: 'green' }))
                                .catch((e) =>
                                  showNotification({
                                    title: 'Ошибка',
                                    message: e instanceof ApiError ? e.message : DEFAULT_ERROR_NOTIFY_MESSAGE,
                                    color: 'red',
                                  }),
                                ),
                          })
                        }
                      >
                        Удалить
                      </Button>
                    ) : null}
                  </Group>
                </Group>
                <Text size="xs">
                  <b>Триггеры:</b> {snippet(r.triggers, 220) || '—'}
                </Text>
                <Text size="xs">
                  <b>Действие:</b> {snippet(r.action, 220) || '—'}
                </Text>
                <Text size="xs">
                  <b>Ответ:</b> {snippet(r.response_from_db, 220) || '—'}
                </Text>
                <Text size="xs">
                  <b>Сбор данных:</b> {snippet(r.data_collected, 160) || '—'}
                </Text>
                <Text size="xs">
                  <b>Вопросы:</b> {snippet(r.questions_asked, 160) || '—'}
                </Text>
                <Group gap="xs">
                  <Text size="xs">
                    <b>Теги:</b> {snippet(r.tags, 100) || '—'}
                  </Text>
                  <Text size="xs">
                    <b>Приоритет:</b> {snippet(r.priority, 30) || '—'}
                  </Text>
                  <Text size="xs">
                    <b>Порядок:</b> {r.order ?? 0}
                  </Text>
                </Group>
              </Stack>
            </Card>
          ))}
        </Stack>
      ) : (
        <ScrollArea>
          <Table striped highlightOnHover withTableBorder miw={1800}>
            <Table.Thead>
              <Table.Tr>
                {canEdit ? <Table.Th w={40} /> : null}
                <Table.Th>Категория</Table.Th>
                <Table.Th>Триггеры</Table.Th>
                <Table.Th>Действие</Table.Th>
                <Table.Th>Ответ из базы</Table.Th>
                <Table.Th>Какие данные собирает</Table.Th>
                <Table.Th>Вопросы</Table.Th>
                <Table.Th>Теги</Table.Th>
                <Table.Th>Приоритет</Table.Th>
                <Table.Th>Порядок</Table.Th>
                <Table.Th>Когда передаёт оператору</Table.Th>
                <Table.Th>Сообщение клиенту при передаче</Table.Th>
                <Table.Th>Статус</Table.Th>
                <Table.Th>Акт.</Table.Th>
                <Table.Th w={180} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filtered.map((r) => (
                <Table.Tr key={r.id}>
                  {canEdit ? (
                    <Table.Td>
                      <Checkbox checked={selected.has(r.id)} onChange={() => toggleSel(r.id)} />
                    </Table.Td>
                  ) : null}
                  <Table.Td>{snippet(r.category, 60) || '—'}</Table.Td>
                  <Table.Td>
                    <Text size="xs" lineClamp={2}>
                      {snippet(r.triggers, 140) || '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" lineClamp={2}>
                      {snippet(r.action, 140) || '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" lineClamp={2}>
                      {snippet(r.response_from_db, 180) || '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" lineClamp={2}>
                      {snippet(r.data_collected, 120) || '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" lineClamp={2}>
                      {snippet(r.questions_asked, 120) || '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td>{snippet(r.tags, 80) || '—'}</Table.Td>
                  <Table.Td>{snippet(r.priority, 30) || '—'}</Table.Td>
                  <Table.Td>{r.order ?? 0}</Table.Td>
                  <Table.Td>
                    <Text size="xs" lineClamp={2}>
                      {snippet(r.when_transfer_to_operator, 140) || '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" lineClamp={2}>
                      {snippet(r.message_to_client_on_transfer, 140) || '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td>{snippet(r.status_on_transfer, 30) || '—'}</Table.Td>
                  <Table.Td>
                    <Group gap={4} wrap="nowrap">
                      <Text size="xs">{r.is_active !== false ? 'да' : 'нет'}</Text>
                      {isTemporaryInstruction(r) ? (
                        <Badge variant="outline" size="xs" color="orange">
                          врем.
                        </Badge>
                      ) : null}
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={6}>
                      <Button size="compact-xs" variant="default" onClick={() => openEdit(r)}>
                        Изменить
                      </Button>
                      {canEdit ? (
                        <Button
                          size="compact-xs"
                          color="red"
                          variant="light"
                          onClick={() =>
                            openConfirmModal({
                              title: 'Удалить инструкцию?',
                              onConfirm: () =>
                                void api
                                  .deleteAIInstruction(projectId, r.id)
                                  .then(() => load())
                                  .then(() => showNotification({ title: 'Удалено', message: '', color: 'green' }))
                                  .catch((e) =>
                                    showNotification({
                                      title: 'Ошибка',
                                      message: e instanceof ApiError ? e.message : DEFAULT_ERROR_NOTIFY_MESSAGE,
                                      color: 'red',
                                    }),
                                  ),
                            })
                          }
                        >
                          Удалить
                        </Button>
                      ) : null}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      )}

      <AppModal
        opened={modal != null}
        onClose={() => setModal(null)}
        title={modal === 'new' ? 'Новая инструкция' : 'Инструкция'}
        size="xl"
        footer={
          <>
            <ActionButton action="cancel" onClick={() => setModal(null)} />
            <ActionButton action="save" loading={saving} onClick={() => void saveInstruction()} />
          </>
        }
      >
        <Stack gap="sm">
          <TextInput label="Категория" value={category} onChange={(e) => setCategory(e.target.value)} />
          <Textarea label="Триггеры" value={triggers} onChange={(e) => setTriggers(e.target.value)} minRows={2} />
          <Textarea label="Действие" value={action} onChange={(e) => setAction(e.target.value)} minRows={2} />
          <Textarea
            label="Ответ из базы"
            value={responseFromDb}
            onChange={(e) => setResponseFromDb(e.target.value)}
            minRows={2}
          />
          <Textarea
            label="Какие данные собирает"
            value={dataCollected}
            onChange={(e) => setDataCollected(e.target.value)}
            minRows={2}
          />
          <Textarea
            label="Вопросы"
            value={questionsAsked}
            onChange={(e) => setQuestionsAsked(e.target.value)}
            minRows={2}
          />
          <TextInput label="Теги" value={tags} onChange={(e) => setTags(e.target.value)} />
          <TextInput label="Приоритет" value={priority} onChange={(e) => setPriority(e.target.value)} />
          <NumberInput label="Порядок" value={order} onChange={setOrder} />
          <Textarea
            label="Когда передаёт оператору"
            value={whenTransfer}
            onChange={(e) => setWhenTransfer(e.target.value)}
            minRows={2}
          />
          <Textarea
            label="Сообщение клиенту при передаче"
            value={messageClient}
            onChange={(e) => setMessageClient(e.target.value)}
            minRows={2}
          />
          <Checkbox
            label={editingTemporary ? 'Активна (по расписанию)' : 'Активна'}
            checked={isActive}
            disabled={editingTemporary}
            onChange={(e) => setIsActive(e.currentTarget.checked)}
          />
          {editingTemporary ? (
            <Text size="xs" c="dimmed">
              Включение/выключение временной инструкции управляется в Актуализации → Временная инструкция.
            </Text>
          ) : null}
        </Stack>
      </AppModal>
    </Stack>
  )
}
